/**
 * UsageBadge.jsx — shared/UsageBadge.jsx
 *
 * Shows accumulated AI API cost in SGD in the topbar.
 * Click to expand a breakdown popup showing:
 *   - All-time token counts and cost (USD + SGD)
 *   - Weekly and monthly call counts vs limits (with progress bars)
 *
 * Usage data is stored in KV (usage:haiku) and incremented server-side.
 * Rate limit counts stored in KV (rl:ai:weekly:YYYY-WW, rl:ai:monthly:YYYY-MM).
 *
 * Pricing tracked per-model: Haiku $0.80/$4.00 per M, Sonnet $3.00/$15.00 per M.
 */

import { useEffect, useState } from 'react';
import { fetchUsage } from '../../lib/api';

const USD_SGD = 1.35;

export default function UsageBadge() {
  const [usage, setUsage] = useState(null);
  const [open,  setOpen]  = useState(false);

  useEffect(() => {
    fetchUsage().then(setUsage).catch(() => {});
  }, []);

  if (!usage) return null;

  const costSgd = ((usage.costUsd || 0) * USD_SGD);

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        title="AI API cost — click for breakdown"
        style={{
          background: 'var(--bg-hover)', border: '1px solid var(--border)',
          color: 'var(--muted)', padding: '3px 9px', borderRadius: 4,
          fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'monospace',
        }}
      >
        S${costSgd.toFixed(4)}
      </button>

      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 998 }} onClick={() => setOpen(false)} />
          <div style={{
            position: 'absolute', right: 0, top: '110%', zIndex: 999,
            background: 'var(--bg-elev)', border: '1px solid var(--border-strong)',
            borderRadius: 7, padding: '14px 16px', minWidth: 260,
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)', fontFamily: 'monospace', fontSize: 12,
          }}>
            <div style={{ fontSize: 10, color: 'var(--muted)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>
              AI API Usage
            </div>

            {/* All-time totals */}
            {[
              ['Total syncs',      usage.calls ?? 0],
              ['Input tokens',     (usage.inputTokens  || 0).toLocaleString()],
              ['Output tokens',    (usage.outputTokens || 0).toLocaleString()],
              ['Total cost (USD)', `$${(usage.costUsd  || 0).toFixed(5)}`],
              ['Total cost (SGD)', `S$${costSgd.toFixed(5)}`],
              ['First call',       usage.firstCall ? new Date(usage.firstCall).toLocaleDateString() : '—'],
              ['Last call',        usage.lastCall  ? new Date(usage.lastCall).toLocaleDateString()  : '—'],
            ].map(([lbl, val]) => (
              <div key={lbl} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ color: 'var(--muted)' }}>{lbl}</span>
                <span style={{ color: 'var(--text)', fontWeight: 600 }}>{val}</span>
              </div>
            ))}

            {/* Weekly / monthly rate limit bars */}
            {usage.limits && (
              <>
                <div style={{ fontSize: 10, color: 'var(--muted)', letterSpacing: '.08em', textTransform: 'uppercase', marginTop: 12, marginBottom: 6 }}>
                  Rate limits
                </div>
                {[
                  ['This week',  usage.limits.weekly],
                  ['This month', usage.limits.monthly],
                ].map(([lbl, lim]) => {
                  if (!lim) return null;
                  const pct   = Math.min((lim.calls / lim.limit) * 100, 100);
                  const color = pct >= 90 ? 'var(--down)' : pct >= 70 ? 'var(--warn)' : 'var(--up)';
                  return (
                    <div key={lbl} style={{ marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                        <span style={{ color: 'var(--muted)', fontSize: 10 }}>{lbl} <span style={{ color: 'var(--faint)' }}>({lim.label})</span></span>
                        <span style={{ color, fontWeight: 700 }}>{lim.calls}/{lim.limit}</span>
                      </div>
                      <div style={{ height: 5, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width .3s' }} />
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 8, lineHeight: 1.6 }}>
              Haiku: $0.80/M in · $4.00/M out<br/>
              Sonnet: $3.00/M in · $15.00/M out<br/>
              1 USD ≈ {USD_SGD} SGD (approx)
            </div>
          </div>
        </>
      )}
    </div>
  );
}
